import React, { useMemo, useState } from "react";
import {
  ComposedChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import { TrendingUp, Users } from "lucide-react";
import { ChartPanel } from "../ui/ChartPanel";
import { CHART_COLORS } from "@/constants/chart-colors";
import { formatSignedCurrency } from "../../utils/chart/chart-period.utils";
import {
  buildGrowthPlanProjection,
  type GrowthPlanMode,
} from "../../utils/chart/growth-plan.utils";
import type { TimelineSnapshot } from "../../types/burndown.types";
import type { ChartData } from "../../utils/chart/financial-cache.utils";

export interface GrowthPlanIncorporateParams {
  monthlyGrowthRatePercent: number;
  monthlyRamp: number;
  projectionType: "growth_rate";
}

interface GrowthPlanChartProps {
  chartData: ChartData;
  currentSnapshot: TimelineSnapshot | null;
  height?: number;
  hasCustomerProjections?: boolean;
  onIncorporateGrowthPlan?: (params: GrowthPlanIncorporateParams) => void;
  onAddCustomerProjection?: () => void;
}

const MODE_OPTIONS: { id: GrowthPlanMode; label: string; hint: string }[] = [
  {
    id: "ramp",
    label: "Growth ramp",
    hint: "Linear revenue increase to reach break-even before cash runs out",
  },
  {
    id: "customer",
    label: "Your plan",
    hint: "Revenue from entered expenses, revenues, and customer projections",
  },
  {
    id: "combined",
    label: "Combined",
    hint: "Whichever is higher: your plan or the required growth ramp",
  },
];

export const GrowthPlanChart: React.FC<GrowthPlanChartProps> = ({
  chartData,
  currentSnapshot,
  height = 360,
  hasCustomerProjections = false,
  onIncorporateGrowthPlan,
  onAddCustomerProjection,
}) => {
  const [mode, setMode] = useState<GrowthPlanMode>(
    hasCustomerProjections ? "combined" : "ramp",
  );
  const [rampOverride, setRampOverride] = useState<string>("");

  const projection = useMemo(() => {
    if (!currentSnapshot) {
      return null;
    }

    const parsedRamp = rampOverride.trim()
      ? Number(rampOverride.replace(/,/g, ""))
      : undefined;

    return buildGrowthPlanProjection({
      chartData,
      currentSnapshot,
      config: {
        mode,
        monthlyRampOverride:
          parsedRamp != null && !Number.isNaN(parsedRamp) && parsedRamp > 0
            ? parsedRamp
            : undefined,
      },
    });
  }, [chartData, currentSnapshot, mode, rampOverride]);

  if (!currentSnapshot || !projection) {
    return null;
  }

  const { breakEvenPlan, points, breakEvenDate, suggestedMonthlyGrowthRatePercent } =
    projection;

  const chartPoints = points.map((point) => ({
    ...point,
    profitPositive: point.netProfit >= 0 ? point.netProfit : 0,
    profitNegative: point.netProfit < 0 ? point.netProfit : 0,
  }));

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ name: string; value: number; color: string }>;
    label?: string;
  }) => {
    if (!active || !payload?.length) {
      return null;
    }

    return (
      <div className="rounded-lg bg-surface-elevated px-3 py-2 text-xs text-white shadow-glow">
        <p className="mb-2 font-semibold">{label}</p>
        {payload.map((entry) => (
          <p key={entry.name} style={{ color: entry.color }}>
            {entry.name}: {formatSignedCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  };

  return (
    <ChartPanel
      title="Growth plan projection"
      subtitle="Revenue ramp, operating profit, and cash trajectory toward break-even"
      actions={
        suggestedMonthlyGrowthRatePercent != null ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {onIncorporateGrowthPlan ? (
              <button
                type="button"
                onClick={() =>
                  onIncorporateGrowthPlan({
                    monthlyGrowthRatePercent: suggestedMonthlyGrowthRatePercent,
                    monthlyRamp: projection.monthlyRamp,
                    projectionType: "growth_rate",
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-[10px] bg-brand/15 px-2.5 py-1.5 text-xs font-semibold text-brand ring-1 ring-brand/25 transition-colors hover:bg-brand/25"
              >
                <Users className="h-3.5 w-3.5" />
                Use {suggestedMonthlyGrowthRatePercent}% in customer projection
              </button>
            ) : null}
            {!hasCustomerProjections && onAddCustomerProjection ? (
              <button
                type="button"
                onClick={onAddCustomerProjection}
                className="inline-flex items-center gap-1.5 rounded-[10px] bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-white/75 ring-1 ring-white/10 transition-colors hover:bg-white/10"
              >
                Add customer projection
              </button>
            ) : null}
          </div>
        ) : null
      }
    >
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {MODE_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setMode(option.id)}
              title={option.hint}
              className={`rounded-[14px] px-3 py-1.5 text-xs font-semibold ring-1 transition-colors ${
                mode === option.id
                  ? "bg-brand/15 text-brand ring-brand/30"
                  : "bg-white/5 text-white/65 ring-white/10 hover:bg-white/10"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Required ramp"
            value={`+$${Math.round(projection.monthlyRamp).toLocaleString()}/mo`}
            tone="warn"
          />
          <MetricCard
            label="Break-even revenue"
            value={`$${Math.round(breakEvenPlan.operatingBreakEvenRevenue).toLocaleString()}/mo`}
          />
          <MetricCard
            label="Break-even month"
            value={
              breakEvenDate
                ? breakEvenDate.toLocaleDateString("en-US", {
                    month: "short",
                    year: "numeric",
                  })
                : breakEvenPlan.isOperatingBreakEven
                  ? "Already there"
                  : "Beyond horizon"
            }
            icon={<TrendingUp className="h-3.5 w-3.5" />}
          />
          <div className="rounded-[14px] bg-surface/30 px-3 py-2 ring-1 ring-white/10">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-white/45">
              Custom ramp ($/mo)
            </label>
            <input
              type="number"
              min={0}
              step={100}
              placeholder={String(Math.round(projection.monthlyRamp))}
              value={rampOverride}
              onChange={(event) => setRampOverride(event.target.value)}
              className="mt-1 w-full rounded-[10px] border border-white/10 bg-surface-elevated px-2 py-1 text-sm text-white outline-none ring-brand/40 focus:ring-2"
            />
          </div>
        </div>

        {suggestedMonthlyGrowthRatePercent != null ? (
          <p className="text-xs text-white/55">
            To model this ramp with customer growth, use about{" "}
            <span className="font-semibold text-brand">
              {suggestedMonthlyGrowthRatePercent}% monthly customer growth
            </span>{" "}
            (when revenue scales with customers).
          </p>
        ) : null}
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={chartPoints} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
          <XAxis
            dataKey="label"
            tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
            interval="preserveStartEnd"
          />
          <YAxis
            yAxisId="monthly"
            tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
          />
          <YAxis
            yAxisId="cash"
            orientation="right"
            tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }}
            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.65)" }} />
          <ReferenceLine yAxisId="monthly" y={0} stroke="rgba(255,255,255,0.2)" />

          <Area
            yAxisId="monthly"
            type="monotone"
            dataKey="burn"
            name="Monthly burn"
            fill={CHART_COLORS.expense}
            fillOpacity={0.15}
            stroke={CHART_COLORS.expense}
            strokeWidth={2}
          />
          <Line
            yAxisId="monthly"
            type="monotone"
            dataKey="baselineRevenue"
            name="Your plan revenue"
            stroke={CHART_COLORS.revenueMuted}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
          />
          <Line
            yAxisId="monthly"
            type="monotone"
            dataKey="projectedRevenue"
            name="Growth plan revenue"
            stroke={CHART_COLORS.cashPositive}
            strokeWidth={2.5}
            dot={false}
          />
          <Bar
            yAxisId="monthly"
            dataKey="profitPositive"
            name="Operating profit"
            fill={CHART_COLORS.revenue}
            fillOpacity={0.85}
            stackId="profit"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            yAxisId="monthly"
            dataKey="profitNegative"
            name="Operating loss"
            fill={CHART_COLORS.expense}
            fillOpacity={0.75}
            stackId="profit"
            radius={[4, 4, 0, 0]}
          />
          <Line
            yAxisId="cash"
            type="monotone"
            dataKey="cumulativeCash"
            name="Projected cash"
            stroke={CHART_COLORS.focus}
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
};

function MetricCard({
  label,
  value,
  tone = "neutral",
  icon,
}: {
  label: string;
  value: string;
  tone?: "neutral" | "warn";
  icon?: React.ReactNode;
}) {
  const toneClass =
    tone === "warn"
      ? "text-warn"
      : "text-white";

  return (
    <div className="rounded-[14px] bg-surface/30 px-3 py-2 ring-1 ring-white/10">
      <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-white/45">
        {icon}
        {label}
      </div>
      <p className={`mt-1 text-sm font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

export default GrowthPlanChart;
